import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { combineLatest, EMPTY, Observable } from 'rxjs';
import { catchError, filter, first, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { Motorization } from 'src/app/api/owcc/models';
import { MotorizationsService, TransferService } from 'src/app/api/owcc/services';
import { AppErrorMajor } from 'src/app/core/app-error/app-error-major.enum';
import { AppError } from 'src/app/core/app-error/app-error.interface';
import { appErrorMajor, appErrorMinorLoading } from 'src/app/core/app-error/store/app-error.actions';
import { OWCC_QUERY_PARAM } from 'src/app/core/config/query-params.config';
import { ActivatedRouteState } from 'src/app/core/services/activated-route/activated-route-state.interface';
import { serializeRoute } from 'src/app/core/services/activated-route/activated-route.utils';
import { FootnotesService } from 'src/app/core/services/footnotes/footnotes.service';
import { LazyComponentId } from 'src/app/core/services/lazy-components/lazy-components-registry';
import { selectApiContext } from 'src/app/select-api-context';
import { FeasibilityResultAlternative } from 'src/app/shared/feasibility-conflict-modal/feasibility-result-alternative.interface';
import {
  AdditionalChangesInitiator,
  FeasibilityResult
} from 'src/app/shared/feasibility-conflict-modal/feasibility-result.interface';
import { ModalService } from 'src/app/shared/modal/modal-service/modal.service';
import {
  invalidateOnlineCode,
  toggleLoadingSpinner,
  updateCurrentMotorization,
  updateStage,
  updateVehicleIdForUpcomingConfiguration
} from 'src/app/store/common/common.actions';
import {
  selectCurrentMotorization,
  selectVehicleIdForUpcomingConfiguration
} from 'src/app/store/common/common.selectors';
import { EffectsBase } from 'src/app/store/effects-base';
import { selectIsInitialMotorization } from 'src/app/store/mbcc/mbcc.selectors';
import { firstValueFromSync, fromRouterAsSnapshot } from 'src/app/store/router/next-router.selectors';
import { RouterActionType } from 'src/app/store/router/router.actions';
import {
  selectBaumuster,
  selectVehicleClassAndBodyFromContext,
  selectVehicleId
} from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { userConfigRemovePostalCode } from 'src/app/store/user-config/user-config.actions';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { WINDOW } from 'src/app/utils/window.token';

import { startUpdateVehicleContextAction } from '../../configuration/store/configuration.actions';

import {
  changeVehicleLengthAction,
  chooseInitialVehicle,
  finishLoadFeasiblityDataAction,
  finishLoadVehiclesAction,
  finishLoadVehiclesByBaumusterAction,
  finishUpdateVehiclesAction,
  reRequestMotorizationForAbTestingAction,
  startLoadFeasiblityDataAction,
  startLoadVehiclesAction,
  startLoadVehiclesByBaumusterAction,
  startUpdateVehiclesAction,
  updateFilterData
} from './motorization.actions';

@Injectable()
export class MotorizationEffects extends EffectsBase {
  public startLoadFeasiblityData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startLoadFeasiblityDataAction),
      tap(() => {
        // TODO: Fix with MBCC-2538
        // eslint-disable-next-line @ngrx/no-dispatch-in-effects
        this.store.dispatch(toggleLoadingSpinner({ isLoading: true }));
      }),
      mergeMap(({ selectedVehicleId, preselectedVehicle }) =>
        this.store.select(selectApiContext).pipe(
          first(),
          switchMap(({ market, subProcess, dataVersion }) =>
            this.transferService
              .transfer({
                market,
                subProcess: subProcess || '',
                dataVersion: dataVersion || '',
                vehicleId: selectedVehicleId || '',
                baumusterNst: preselectedVehicle?.modelId || ''
              })
              .pipe(
                map(feasibilityData => finishLoadFeasiblityDataAction({ feasibilityData, preselectedVehicle })),
                catchError((error: HttpErrorResponse) => [
                  toggleLoadingSpinner({ isLoading: false }),
                  appErrorMinorLoading({ error: error })
                ])
              )
          )
        )
      )
    )
  );

  public finishLoadFeasiblityData$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(finishLoadFeasiblityDataAction),
        map(({ feasibilityData, preselectedVehicle }) => {
          // TODO: Fix with MBCC-2538
          // eslint-disable-next-line @ngrx/no-dispatch-in-effects
          this.store.dispatch(toggleLoadingSpinner({ isLoading: false }));
          const modalData: FeasibilityResult = {
            alternatives: [feasibilityData.target as FeasibilityResultAlternative],
            fastSelect: feasibilityData.fastSelect,
            changeInitiator: AdditionalChangesInitiator.MotoTransfer,
            vehicle: preselectedVehicle,
            selectedBaumusterNst: preselectedVehicle?.modelId || ''
          };
          this.modalService.open(LazyComponentId.FeasibilityConflictModal, modalData);
        })
      ),
    { dispatch: false }
  );

  /**
   * When navigating to Motorization, load necessary Data
   */
  public routeChange$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(RouterActionType.ContextUpdated),
        filter(
          ({ routeState }: { routeState: ActivatedRouteState }) =>
            routeState.isMotorization || routeState.isVehicleLength
        ),
        map(({ routeState }: { routeState: ActivatedRouteState }) => {
          this.loadMotorizations(routeState);
        })
      ),
    { dispatch: false }
  );

  public reRequestMotorizationForAbTesting$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(reRequestMotorizationForAbTestingAction),
        map(({ snapshot }): ActivatedRouteState => serializeRoute(snapshot)),
        filter(routeState => routeState.isMotorization || routeState.isVehicleLength),
        map(routeState => {
          this.loadMotorizations(routeState);
        })
      ),
    { dispatch: false }
  );

  /**
   * Load Motorization Vehicles and ClassBodyNames from Backend
   */
  public loadVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startLoadVehiclesAction),
      mergeMap(() =>
        // Question: do we really need to load motorization each time?
        combineLatest([
          this.store.select(selectApiContext).pipe(first()),
          this.store.select(selectVehicleClassAndBodyFromContext).pipe(
            filter(({ vehicleClass, vehicleBody }) => !!vehicleClass && !!vehicleBody),
            map(({ vehicleClass, vehicleBody }) => ({
              vehicleClass,
              vehicleBody
            })),
            first()
          )
        ]).pipe(
          first(),
          switchMap(([{ market, subProcess, dataVersion }, context]) =>
            this.motorizationsService
              .motorizationByClassAndBody({
                market,
                subProcess: subProcess || '',
                dataVersion: dataVersion || '',
                vehicleClass: context?.vehicleClass?.toUpperCase() || '',
                vehicleBody: context?.vehicleBody?.toUpperCase() || ''
              })
              .pipe(filter(motorization => !!motorization))
          ),
          map((motorization: Motorization) => {
            /**
             * Deep copy object to ensure a later change of the vehicleId does not change the original motorization vehicle.
             * (important for determining if a transfer modal must be shown on motorization change)
             */
            let selectedVehicle = cloneDeep(
              motorization?.vehicles?.filter(vehicle => vehicle.preselected === true).pop() ||
                (motorization?.vehicles || [])[0]
            );

            const vehicleIdFromUrlParam: string | undefined = firstValueFromSync<string | undefined>(
              this.store.pipe(fromRouterAsSnapshot.selectVehicleIdQueryParam)
            );

            // If vehicleId is set as url param, load the motorization vehicle data for the new vehicleId (must happen before looking at the store current vehicle)
            if (vehicleIdFromUrlParam) {
              selectedVehicle.vehicleId = vehicleIdFromUrlParam;

              // once evaluated we remove it to not drag the long parameter in the URL through the app, leading to issues when sending the URL to CRM or other endpoints
              this.window.history.replaceState(
                {},
                '',
                (() => {
                  const currentUrl = new URL(this.window.location.href);
                  const params = new URLSearchParams(currentUrl.search);
                  params.delete(OWCC_QUERY_PARAM.VEHICLE_ID);
                  currentUrl.search = params.toString();
                  return currentUrl.href;
                })()
              );
            }

            // TODO: Fix with MBCC-2538
            // prevent subscribe within subscribe
            this.store
              .select(selectCurrentMotorization)
              .pipe(filterTruthy(), first())
              .subscribe(vehicle => {
                if (vehicle) selectedVehicle = vehicle;
              });

            // TODO: Fix with MBCC-2538
            // eslint-disable-next-line @ngrx/no-dispatch-in-effects
            this.store.dispatch(updateCurrentMotorization({ vehicle: selectedVehicle }));

            if (motorization?.filters && motorization?.sortingKeys && motorization.sortingKeys.length > 0) {
              // TODO: Fix with MBCC-2538
              // eslint-disable-next-line @ngrx/no-dispatch-in-effects
              this.store.dispatch(
                updateFilterData({ filters: motorization.filters, sortingKeys: motorization.sortingKeys })
              );
            }

            return finishLoadVehiclesAction({ motorization });
          }),
          catchError((error: HttpErrorResponse) => {
            const errorConfigurator: AppError = {
              statusCode: error.status,
              message: error.message,
              name: error.name,
              type: AppErrorMajor.LoadMotorization
            };

            // in case of an error reset stored postcode to not end up in a endless loop of error (with error pages)
            // TODO: Fix with MBCC-2538
            // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially, @ngrx/no-dispatch-in-effects
            this.store.dispatch(userConfigRemovePostalCode());
            // TODO: Fix with MBCC-2538
            // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially, @ngrx/no-dispatch-in-effects
            this.store.dispatch(appErrorMajor({ error: errorConfigurator }));
            return EMPTY;
          })
        )
      )
    )
  );

  /**
   * Update Motorization Vehicles and ClassBodyNames from Backend
   */
  public updateVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startUpdateVehiclesAction),
      map(({ motorization: payload }) =>
        finishUpdateVehiclesAction({ motorization: payload || { vehicles: [], classBodyNames: [] } })
      )
    )
  );

  /**
   * Load Motorization Vehicles and ClassBodyNames from Backend via baumuster
   */
  public loadVehiclesByBaumuster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startLoadVehiclesByBaumusterAction),
      mergeMap(() =>
        combineLatest([
          this.store.select(selectApiContext).pipe(first()),
          this.store.select(selectBaumuster).pipe(
            filter(baumuster => !!baumuster),
            map(baumuster => baumuster),
            first()
          )
        ]).pipe(
          switchMap(([{ market, subProcess, dataVersion }, baumuster]) =>
            this.motorizationsService.motorizationByBaumusterNst({
              market,
              subProcess: subProcess || '',
              dataVersion: dataVersion || '',
              baumusterNst: baumuster || ''
            })
          ),
          map((motorization: Motorization) => {
            /**
             * Deep copy object to ensure a later change of the vehicleId does not change the original motorization vehicle.
             * (important for determining if a transfer modal must be shown on motorization change)
             */
            const selectedVehicle = cloneDeep(
              motorization?.vehicles?.filter(vehicle => vehicle.preselected === true).pop() ||
                (motorization?.vehicles || [])[0]
            );

            const vehicleIdFromUrlParam: string | undefined = firstValueFromSync<string | undefined>(
              this.store.pipe(fromRouterAsSnapshot.selectVehicleIdQueryParam)
            );

            // If vehicleId is set as url param, load the motorization vehicle data for the new vehicleId (must happen before looking at the store current vehicle)
            if (vehicleIdFromUrlParam) {
              selectedVehicle.vehicleId = vehicleIdFromUrlParam;
            }

            // Get vehicleId from urlParam. if modelId matches any motorization, overwrite vehicleId of selectedVehicle before dispatch
            // TODO: Fix with MBCC-2538
            // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially, @ngrx/no-dispatch-in-effects
            this.store.dispatch(updateCurrentMotorization({ vehicle: selectedVehicle }));
            // TODO: Fix with MBCC-2538
            // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially, @ngrx/no-dispatch-in-effects
            this.store.dispatch(
              updateFilterData({ filters: motorization.filters || {}, sortingKeys: motorization.sortingKeys || [] })
            );

            return finishLoadVehiclesByBaumusterAction({ motorization });
          }),
          catchError((error: HttpErrorResponse) => {
            const errorConfigurator: AppError = {
              statusCode: error.status,
              message: error.message,
              name: error.name,
              type: AppErrorMajor.ResolveBaumuster
            };

            // TODO: Fix with MBCC-2538
            // eslint-disable-next-line @ngrx/no-dispatch-in-effects
            this.store.dispatch(appErrorMajor({ error: errorConfigurator }));
            return EMPTY;
          })
        )
      )
    )
  );

  public updateFootnotesFromMotorization$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(finishLoadVehiclesAction, finishLoadVehiclesByBaumusterAction),
        map(({ motorization }) => {
          // register footnotes contained in motorization response. This replaces footnotes from CARL (if present)
          for (const hint of Object.keys(motorization?.footnotes || [])) {
            if (hint && motorization?.footnotes?.[hint]) {
              this.footnoteService.registerFootnote(hint, motorization.footnotes[hint]);
            }
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Start update vehicles after loading
   */
  public triggerUpdateVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(finishLoadVehiclesByBaumusterAction, finishLoadVehiclesAction),
      map(({ motorization: payload }) => startUpdateVehiclesAction({ motorization: payload }))
    )
  );

  public selectUpcomingConfiguration$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(finishUpdateVehiclesAction),
        map(action => action.motorization.vehicles),
        concatLatestFrom(() => this.store.select(selectVehicleIdForUpcomingConfiguration)),
        map(([vehicles, vehicleId]) => {
          if (vehicles && !vehicleId) {
            const preselectedVehicle = vehicles.find(vehicle => vehicle.preselected === true) || vehicles[0];

            if (preselectedVehicle) {
              // TODO: Fix with MBCC-2538
              // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially, @ngrx/no-dispatch-in-effects
              this.store.dispatch(
                // assuming we always have a valid vehicleClassId and vehicleBodyId here, fallbacks (empty strings) do never apply!
                startUpdateVehicleContextAction({
                  vehicleClass: preselectedVehicle.vehicleClassId || '',
                  vehicleBody: preselectedVehicle.vehicleBodyId || '',
                  vehicleId: preselectedVehicle.vehicleId || ''
                })
              );

              // TODO: Fix with MBCC-2538
              // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially, @ngrx/no-dispatch-in-effects
              this.store.dispatch(
                updateVehicleIdForUpcomingConfiguration({ vehicleId: preselectedVehicle.vehicleId || '' })
              );
            }
          }
        })
      ),
    { dispatch: false }
  );

  public chooseInitialVehicle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chooseInitialVehicle),
        // TODO: Fix with MBCC-2538
        // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
        switchMap(({ vehicle }) => [
          updateVehicleIdForUpcomingConfiguration({ vehicleId: vehicle?.vehicleId || '' }),
          updateCurrentMotorization({ vehicle: vehicle }),
          updateStage({ stage: vehicle?.stageV2, enforceReset: false }),
          invalidateOnlineCode()
        ])
      ),
    { dispatch: true }
  );

  public changeVehicleLength$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changeVehicleLengthAction),
        switchMap(payload =>
          this.store.select(selectVehicleId).pipe(
            first(),
            concatLatestFrom(() => this.store.select(selectIsInitialMotorization)),
            map(([id, isInitialMotorization]) =>
              // to check if the initial vehicle was adjusted by the user
              isInitialMotorization
                ? chooseInitialVehicle({ vehicle: payload.motorizationVehicle })
                : startLoadFeasiblityDataAction({
                    selectedVehicleId: id || '',
                    preselectedVehicle: payload.motorizationVehicle
                  })
            )
          )
        )
      ),
    { dispatch: true }
  );

  public constructor(
    private readonly store: Store,
    private readonly transferService: TransferService,
    private readonly modalService: ModalService,
    private readonly footnoteService: FootnotesService,
    private readonly motorizationsService: MotorizationsService,
    @Inject(WINDOW) private readonly window: Window
  ) {
    super();
  }

  private loadMotorizations(routeState: ActivatedRouteState): void {
    if (routeState.pathParams.baumuster) this.store.dispatch(startLoadVehiclesByBaumusterAction());
    else this.store.dispatch(startLoadVehiclesAction());
  }
}
